import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Spinner, Text, VStack } from '@chakra-ui/react';

interface InfiniteScrollContainerProps {
  children: React.ReactNode;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  threshold?: number;
}

export const InfiniteScrollContainer: React.FC<InfiniteScrollContainerProps> = ({
  children,
  hasMore,
  loadingMore,
  onLoadMore,
  threshold = 200
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !loadingMore) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    if (!sentinelRef.current) return;

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: `${threshold}px`,
      threshold: 0.1
    });

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, threshold]);

  return (
    <Box ref={containerRef} width="100%">
      {children}
      
      {/* Sentinel para detectar scroll infinito */}
      <Box ref={sentinelRef} height="20px" />
      
      {/* Indicador de carga */}
      {loadingMore && (
        <VStack py={4} spacing={2}>
          <Spinner size="md" color="blue.500" />
          <Text fontSize="sm" color="gray.600">
            Cargando más predios...
          </Text>
        </VStack>
      )}
      
      {/* Mensaje de fin */}
      {!hasMore && !loadingMore && (
        <VStack py={4}>
          <Text fontSize="sm" color="gray.500">
            No hay más predios para mostrar
          </Text>
        </VStack>
      )}
    </Box>
  );
};